import type { ViewType } from 'owa-calendar-actions';
import { action } from 'satcheljs';
import type {
    NotificationButtonOptions,
    NotificationOptions,
    DefaultHotKeyCommand,
} from 'owa-notification-bar-force-dismiss';
import type { HotkeyCommand, Hotkeys } from 'owa-hotkeys';

/**
 * Show a notification with an action button that calls the given callback
 *
 * @param actionName the name of the notification type
 * @param actionSource the ViewType from which this notification was triggered
 * @param messageString the string that gets shown in the notification
 * @param primaryActionText the string to show in the action button in the notification
 * @param primaryActionClickedCallback the callback function to call if the action button is clicked
 * @param icon the icon to show in the notification bar
 */
export const showNotificationWithButtonCallback = action(
    'CALENDAR_SHOW_NOTIFICATION_WITH_BUTTON_CALLBACK',
    (
        actionName: string,
        actionSource: ViewType,
        messageString: string,
        primaryActionText: string,
        primaryActionClickedCallback: () => void,
        icon?: string,
        primaryActionHotKeyCommand?: HotkeyCommand | Hotkeys | DefaultHotKeyCommand
    ) => ({
        actionName,
        actionSource,
        messageString,
        primaryActionText,
        primaryActionClickedCallback,
        icon,
        primaryActionHotKeyCommand,
    })
);

/**
 * Show a notification with the specified options. The default is to show a notification with the given messageString. Default behavior
 * is to create a notification with no icon at the bottom of the screen that auto dismisses after 5 seconds
 *
 * @param actionName the name of the notification type
 * @param actionSource the ViewType from which this notification was triggered
 * @param messageString the string that gets shown in the notification
 * @param notificationOptions the options to customize the notification bar itself
 * @param actionButtonOptions the options for the action button in the notification bar, if any
 */
export const showNotification = action(
    'CALENDAR_SHOW_NOTIFICATION',
    (
        actionName: string,
        actionSource: ViewType,
        messageString: string,
        notificationOptions?: NotificationOptions,
        actionButtonOptions?: NotificationButtonOptions
    ) => ({
        actionName,
        actionSource,
        messageString,
        notificationOptions,
        actionButtonOptions,
    })
);

/**
 * Dismiss the notification for the given action type prematurely.
 *
 * @param actionName the name of the notification type
 */
export const dismissNotification = action(
    'CALENDAR_DISMISS_NOTIFICATION',
    (actionName: string) => ({
        actionName,
    })
);

/**
 * Sets whether the notification bar to show calendar notifications is enabled
 *
 * @param enabled true to enable the bar, else false
 */
export const enableNotificationBar = action(
    'CALENDAR_ENABLE_NOTIFICATION_BAR',
    (enabled: boolean) => ({
        enabled,
    })
);
